import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import useRoutesHelper from "../../../../hooks/useRoutesHelper";
import ContentCard from "../../../../components/misc/ContentCard";
import { clearFleetAssignedCarToNode } from "../../../../redux/reducer/fleet.reducer";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../../helpers/apiHelper";
import Dropdown, { DropdownValue } from "../../../../components/misc/Dropdown";
import FormButtons from "../../../../components/misc/FormButtons";
import {
  fetchFleet,
  postCreateOrUpdateAssignedCar,
} from "../../../../redux/api/fleet.api";
import { getWorkingRoutesNodes } from "../../../../redux/api/expedition.api";

interface initialValuesInterface {
  selectedCar: DropdownValue | null;
  selectedNode: DropdownValue | null;
}

const initialValues: initialValuesInterface = {
  selectedCar: null,
  selectedNode: null,
};

const formikSchema = Yup.object({
  selectedCar: Yup.object({
    id: Yup.number().required(),
    label: Yup.string().required(),
  })
    .nullable()
    .required("Trebuie sa alegeti o masina"),

  selectedNode: Yup.object({
    id: Yup.number().required(),
    label: Yup.string().required(),
  })
    .nullable()
    .required("Trebuie sa alegeti un nod"),
});

function AssignCarToNodeCreateOrUpdate({
  withUpdate,
}: {
  withUpdate?: boolean;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { routeMainParam } = useRoutesHelper();
  const { id_flota_nod } = routeMainParam as { id_flota_nod: string | number };

  const {
    fleet: {
      fleetAssignedCarToNode,
      fleetData: {
        data: { data: fleetData },
        isLoading: isLoadingFleet,
      },
    },
    expedition: {
      workingRoutesNodes: {
        data: { data: workingRoutesNodes },
      },
    },
  } = useAppSelector((state) => ({
    fleet: state.fleet,
    expedition: state.expedition,
  }));

  const [formikValues, setFormikValues] =
    useState<initialValuesInterface>(initialValues);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (id_flota_nod && fleetAssignedCarToNode) {
      setFormikValues((prev) => ({
        ...prev,
        selectedCar: {
          id: fleetAssignedCarToNode.id_auto,
          label: fleetAssignedCarToNode.nrauto,
        },
        selectedNode: {
          id: fleetAssignedCarToNode.id_nod,
          label: fleetAssignedCarToNode.nodNume,
        },
      }));
    }
  }, [id_flota_nod, fleetAssignedCarToNode]);

  useEffect(() => {
    dispatch(
      fetchFleet({
        page: 1,
        perPage: 9999,
      }),
    );

    dispatch(
      getWorkingRoutesNodes({
        page: 1,
        perPage: 50,
      }),
    );

    return () => {
      dispatch(clearFleetAssignedCarToNode());
    };
  }, [dispatch, fleetAssignedCarToNode]);

  useEffect(() => {
    if (fleetAssignedCarToNode && withUpdate) {
      setFormikValues((prev) => ({
        ...prev,
        id_auto: fleetAssignedCarToNode.id_auto,
        id_nod: fleetAssignedCarToNode.id_nod,
      }));
    }
  }, [withUpdate, fleetAssignedCarToNode, id_flota_nod]);

  const handleSaveProductDetails = async (values: initialValuesInterface) => {
    setIsLoading(true);

    const resultAction = await dispatch(
      postCreateOrUpdateAssignedCar({
        ...values,
        id_auto: values.selectedCar?.id,
        id_nod: values.selectedNode?.id,
        id_flota_nod: id_flota_nod,
      }),
    );

    if (
      checkEndpointStatus(resultAction, postCreateOrUpdateAssignedCar) !==
      endpointStatus.pending
    ) {
      setIsLoading(false);
    }

    if (
      checkEndpointStatus(resultAction, postCreateOrUpdateAssignedCar) ===
      endpointStatus.fulfilled
    ) {
      navigate(-1);
    }
  };

  const fleetDataDropdownValues = useMemo(
    () =>
      fleetData?.map((item) => ({
        id: item.id_auto,
        label: item.nrauto,
      })),
    [fleetData],
  );

  const nodesDropdownValues = useMemo(
    () =>
      workingRoutesNodes?.map((item) => ({
        id: item.id_nod,
        label: item.nodNume,
      })),
    [workingRoutesNodes],
  );

  const handleGoBack = () => {
    navigate(-1);
  };

  if (!fleetAssignedCarToNode && withUpdate) {
    return (
      <ContentCard>
        <h1>Document inexistent</h1>
      </ContentCard>
    );
  }

  const title = withUpdate
    ? `Transfer pentru masina ${fleetAssignedCarToNode?.marca}, model ${fleetAssignedCarToNode?.model}, nr.auto ${fleetAssignedCarToNode?.nrauto}`
    : "Adauga masina";

  return (
    <>
      <ContentCard
        cardTitle={title}
        cardHeaderClassName="text-center"
        isLoading={isLoadingFleet || isLoading}
        isLoadingClassName="d-flex flex-column justify-content-center align-items-center vh-75"
        CardHeader={() => <></>}>
        <Formik
          enableReinitialize
          initialValues={formikValues}
          validationSchema={formikSchema}
          onSubmit={handleSaveProductDetails}>
          {({
            values,
            isSubmitting,
            handleSubmit,
            setFieldValue,
            errors,
            touched,
          }) => {
            const { selectedCar, selectedNode } = values;

            return (
              <form
                className="form w-100"
                onSubmit={handleSubmit}
                noValidate
                id="kt_login_signin_form">
                <Dropdown
                  className="my-4"
                  title="Selecteaza masina"
                  data={fleetDataDropdownValues}
                  singleValue={selectedCar}
                  setSingleValue={(value) => {
                    setFieldValue("selectedCar", value);
                  }}
                  error={errors["selectedCar"]}
                  touched={touched["selectedCar"]}
                  disabled={withUpdate}
                />

                <Dropdown
                  className="my-4 mt-10"
                  title="Nod"
                  data={nodesDropdownValues}
                  singleValue={selectedNode}
                  setSingleValue={(value) => {
                    setFieldValue("selectedNode", value);
                  }}
                  error={errors["selectedNode"]}
                  touched={touched["selectedNode"]}
                />

                <FormButtons
                  goBackLabel="Inapoi"
                  goNextLabel={isSubmitting ? "Se incarca..." : "Salveaza"}
                  nextButtonDisabled={!selectedCar || !selectedNode}
                  nextButtonLoading={
                    isSubmitting || isLoading || isLoadingFleet
                  }
                  handleGoBack={handleGoBack}
                />
              </form>
            );
          }}
        </Formik>
      </ContentCard>
    </>
  );
}

export default AssignCarToNodeCreateOrUpdate;
